import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

// Hook for scroll-triggered animations
export const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      } else if (options.once === false) {
        setIsInView(false);
      }
    }, { threshold: options.threshold || 0.1, rootMargin: options.rootMargin || '0px' });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin, options.once]);

  return [ref, isInView];
};

export const AnimatedSection = ({ children, className, delay = 0, animation = 'fadeUp' }) => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  
  const animations = {
    fadeUp: {
      initial: 'opacity-0 translate-y-12',
      animate: 'opacity-100 translate-y-0'
    },
    fadeIn: {
      initial: 'opacity-0',
      animate: 'opacity-100'
    },
    slideLeft: {
      initial: 'opacity-0 translate-x-12',
      animate: 'opacity-100 translate-x-0'
    },
    slideRight: {
      initial: 'opacity-0 -translate-x-12',
      animate: 'opacity-100 translate-x-0'
    },
    scale: {
      initial: 'opacity-0 scale-95',
      animate: 'opacity-100 scale-100'
    }
  };

  const anim = animations[animation] || animations.fadeUp;

  return (
    <div 
      ref={ref}
      className={cn(
        "transition-all duration-1000 ease-out",
        isInView ? anim.animate : anim.initial,
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};


